import { CompanyTripReportDto } from '@/__generated';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER } from '@/config';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Col, Form, Row, Select, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { exportService } from '@/services/exportService';
import { ExportExcelBtn, ExportPdfBtn } from '@/components/app/ExportBtn';

type Props = {};
const PAGE_TITLE = 'Trips Report';

const columns: ColumnsType<CompanyTripReportDto> = [
  {
    get title() { return t('Date'); },
    dataIndex: 'date',
    width: 150,
    fixed: 'left',
    render: v => <b>{dateTimeDisplayFormat.dateOnly(v)}</b>,
  },
  { get title() { return t('No. of Trip'); }, dataIndex: 'noOfTrip', width: 150 },
  { get title() { return t('No. of Bus'); }, dataIndex: 'noOfBus', width: 150 },
  { get title() { return t('Distance'); }, dataIndex: 'distance', width: 150, render: v => `${v} kms` },
  { get title() { return t('No. of Passenger'); }, dataIndex: 'noOfPassenger', width: 200 },
  { get title() { return t('No. of Shipment'); }, dataIndex: 'noOfShipment', width: 200 },
  { get title() { return t('No. of Booking'); }, dataIndex: 'noOfBooking', width: 200 },
];

const TripsReportPage = (props: Props) => {
  const [searchForm] = Form.useForm();
  const [report, setReport] = React.useState<CompanyTripReportDto[]>();
  const [loading, setIsLoading] = React.useState(false);
  const [routerOptions, setRouterOptions] = React.useState<{ label: any; value: any }[]>([]);
  const [filter, setFilter] = React.useState<{ fromDate: string; toDate: string; routeId?: string }>({
    fromDate: formatApiDate(moment().add(-1, 'month')),
    toDate: formatApiDate(moment()),
  });

  React.useEffect(() => {
    apiSdk.CompanyTripReportService.getRoutesByUsers().then(res => setRouterOptions(res.map(res => ({ label: res.name, value: res.id }))));
  }, []);
  React.useEffect(() => {
    setIsLoading(true);

    apiSdk.CompanyTripReportService.getTripReports({
      fromDate: filter?.fromDate,
      toDate: filter?.toDate,
      routeId: filter.routeId,
    })
      .then(res => {
        setReport(res);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const onSearch = ({ fromDate, toDate, routeId }: any) => {
    setFilter({
      fromDate: formatApiDate(fromDate),
      toDate: formatApiDate(toDate),
      routeId: routeId,
    });
  };

  const onExportExcel = () => {
    searchForm.validateFields().then(res => {
      exportService.exportTripReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toExcel();
    });
  };
  const onExportPdf = () => {
    searchForm.validateFields().then(res => {
      exportService.exportTripReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toPdf();
    });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
          <ExportExcelBtn onClick={onExportExcel} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Form
          layout="inline"
          className="nta-custom-form"
          form={searchForm}
          colon={false}
          onFinish={onSearch}
          initialValues={filter}
          style={{ marginTop: 10 }}
        >
          <Row gutter={ROW_GUTTER}>
            <Col md={24} xl={24} xxl={24}>
              <Space>
                <Form.Item name="fromDate" label={t('From date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="toDate" label={t('To Date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="routeId" label={t('Route')}>
                  <Select options={routerOptions} style={{ width: 300 }} allowClear={true} placeholder={t('Select route')} />
                </Form.Item>
                <Form.Item style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>

        <Table className="transport-report-table" loading={loading} size="small" bordered columns={columns} dataSource={report} pagination={false} />
      </div>
    </MainContent>
  );
};
export default TripsReportPage;
